import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Key, Plus, Trash, PencilSimple, Copy, Check, Eye, EyeSlash,
  LockKey, ShieldCheck, Broadcast,
} from "@phosphor-icons/react";

// ---------- Small helpers ----------
function IconBtn({ onClick, children, title, danger, testid }) {
  return (
    <button
      onClick={onClick} title={title} data-testid={testid}
      className={`w-8 h-8 flex items-center justify-center border border-border hover:bg-secondary transition-colors ${danger ? "hover:border-[#E00000] hover:text-[#E00000]" : ""}`}
    >{children}</button>
  );
}

function CopyBtn({ text, testid }) {
  const [c, setC] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
        setC(true); setTimeout(() => setC(false), 1200);
      }}
      title="Copy" data-testid={testid}
      className="p-1.5 border border-border hover:bg-secondary"
    >{c ? <Check size={12} className="text-[#008A00]" /> : <Copy size={12} />}</button>
  );
}

function Modal({ open, onClose, title, children, width = "max-w-2xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className={`bg-white border border-border ${width} w-full max-h-[92vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

const KIND_LABEL = {
  api_key: "API Key",
  webhook_url: "Webhook URL",
  bearer_token: "Bearer Token",
  oauth_token: "OAuth Token",
  custom: "Custom",
};

// ---------- Create / Edit modal ----------
function CredentialModal({ open, onClose, editing, kinds, services, onSaved }) {
  const [form, setForm] = useState({
    name: "", service: "custom", kind: "api_key", value: "", notes: "", active: true,
  });
  const [showValue, setShowValue] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        name: editing.name || "", service: editing.service || "custom",
        kind: editing.kind || "api_key", value: "",  // never pre-fill; user must re-enter to change
        notes: editing.notes || "", active: editing.active !== false,
      } : { name: "", service: "custom", kind: "api_key", value: "", notes: "", active: true });
      setShowValue(false);
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!editing && !form.value) { toast.error("Value is required"); return; }
    setBusy(true);
    try {
      if (editing?.id) {
        // Only send value if user entered a new one
        const payload = { ...form };
        if (!payload.value) delete payload.value;
        await api.patch(`/credentials/${editing.id}`, payload);
        toast.success("Credential updated");
      } else {
        await api.post("/credentials", form);
        toast.success("Credential saved (encrypted)");
      }
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? `Edit · ${editing.name}` : "New credential"}>
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Name</label>
            <input
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Slack · #ops channel URL"
              data-testid="credential-name-input"
              className="w-full mt-1 border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Service</label>
            <input
              list="services-list"
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value.toLowerCase() })}
              placeholder="slack, zapier, easyinsights…"
              className="w-full mt-1 border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
            <datalist id="services-list">
              {services.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Kind</label>
            <select
              value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="w-full mt-1 border border-border px-3 py-2 text-sm bg-white focus:border-primary focus:outline-none"
            >
              {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] || k}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-primary" />
              Active
            </label>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-1.5">
            <LockKey size={11} /> Value {editing && <span className="text-muted-foreground normal-case tracking-normal">(leave blank to keep current)</span>}
          </label>
          <div className="mt-1 relative">
            <input
              type={showValue ? "text" : "password"}
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              placeholder={editing ? "•••••••••••••" : "Paste the API key / URL / token"}
              data-testid="credential-value-input"
              className="w-full border border-border px-3 py-2 pr-10 text-sm font-mono focus:border-primary focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShowValue(!showValue)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
            >{showValue ? <EyeSlash size={14} /> : <Eye size={14} />}</button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1">
            <ShieldCheck size={11} className="text-[#008A00]" />
            Stored encrypted (Fernet). Only decrypted when explicitly revealed by an admin.
          </p>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Notes (optional)</label>
          <textarea
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2} placeholder="Where you generated it, expiry, scope, etc."
            className="w-full mt-1 border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary">Cancel</button>
        <button
          onClick={save} disabled={busy}
          data-testid="credential-save-button"
          className="px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >{busy ? "Saving…" : editing ? "Save changes" : "Save credential"}</button>
      </div>
    </Modal>
  );
}

// ---------- Row with reveal-on-click ----------
function CredentialRow({ item, onEdit, onDelete, onTest, onRefresh }) {
  const [revealed, setRevealed] = useState(null);
  const doReveal = async () => {
    if (revealed) { setRevealed(null); return; }
    try {
      const { data } = await api.get(`/credentials/${item.id}?reveal=true`);
      setRevealed(data.value);
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const shown = revealed || item.value_masked || "";

  return (
    <div className="px-5 py-4" data-testid={`credential-${item.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{item.name}</span>
            <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 border border-border">
              {item.service}
            </span>
            <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 bg-primary/5 text-primary border border-primary/20">
              {KIND_LABEL[item.kind] || item.kind}
            </span>
            {!item.active && (
              <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 border border-muted-foreground/30 text-muted-foreground">
                Paused
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <code className="flex-1 px-3 py-1.5 border border-border bg-secondary/40 font-mono text-xs break-all">
              {shown || <span className="text-muted-foreground italic">(empty)</span>}
            </code>
            <button
              onClick={doReveal}
              title={revealed ? "Hide" : "Reveal"}
              className="p-1.5 border border-border hover:bg-secondary"
              data-testid={`reveal-${item.id}`}
            >{revealed ? <EyeSlash size={12} /> : <Eye size={12} />}</button>
            {revealed && <CopyBtn text={revealed} testid={`copy-${item.id}`} />}
          </div>
          {item.notes && (
            <div className="text-xs text-muted-foreground">{item.notes}</div>
          )}
          {item.last_used_at && (
            <div className="text-[10px] text-muted-foreground">
              Last revealed: {new Date(item.last_used_at).toLocaleString()}
            </div>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <IconBtn onClick={onTest} title="Test connection" testid={`test-cred-${item.id}`}><Broadcast size={14} /></IconBtn>
          <IconBtn onClick={onEdit} title="Edit" testid={`edit-cred-${item.id}`}><PencilSimple size={14} /></IconBtn>
          <IconBtn onClick={onDelete} title="Delete" testid={`delete-cred-${item.id}`} danger><Trash size={14} /></IconBtn>
        </div>
      </div>
    </div>
  );
}

// ---------- Main component ----------
export default function CredentialsVault() {
  const [items, setItems] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ service: "", kind: "" });

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.service) params.set("service", filter.service);
      if (filter.kind) params.set("kind", filter.kind);
      const [list, meta] = await Promise.all([
        api.get(`/credentials?${params.toString()}`),
        api.get("/credentials/meta"),
      ]);
      setItems(list.data || []);
      setKinds(meta.data.kinds || []);
      setServices(meta.data.services || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter.service, filter.kind]);

  const del = async (item) => {
    if (!confirm(`Delete "${item.name}"? Any feature referencing it will need updating.`)) return;
    try {
      await api.delete(`/credentials/${item.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const test = async (item) => {
    toast.message(`Testing "${item.name}"…`);
    try {
      const { data } = await api.post(`/credentials/${item.id}/test`);
      if (data.ok === true) toast.success(`✓ ${data.message}`);
      else if (data.ok === false) toast.error(`✗ ${data.message}`);
      else toast.message(`${data.message}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-border bg-white" data-testid="credentials-vault-section">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Vault · Encrypted at rest</div>
          <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2 mt-0.5">
            <Key size={16} weight="fill" className="text-primary" />
            API keys &amp; credentials
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filter.service}
            onChange={(e) => setFilter({ ...filter, service: e.target.value })}
            className="text-xs border border-border px-2 py-1.5 bg-white focus:border-primary focus:outline-none"
            data-testid="filter-service"
          >
            <option value="">All services</option>
            {services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={filter.kind}
            onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
            className="text-xs border border-border px-2 py-1.5 bg-white focus:border-primary focus:outline-none"
            data-testid="filter-kind"
          >
            <option value="">All kinds</option>
            {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] || k}</option>)}
          </select>
          <button
            onClick={() => { setEditing(null); setModalOpen(true); }}
            data-testid="new-credential-button"
            className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
          ><Plus size={14} weight="bold" /> New credential</button>
        </div>
      </div>

      {loading && <div className="px-5 py-8 text-xs text-muted-foreground text-center">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="px-5 py-10 text-center">
          <Key size={32} className="mx-auto text-muted-foreground/50" />
          <p className="text-sm font-semibold mt-3">No credentials saved yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Store API keys, webhook URLs, and bearer tokens here once — then reuse them across features (outbound webhooks, integrations, etc). Encrypted at rest.
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="divide-y divide-border">
          {items.map((item) => (
            <CredentialRow
              key={item.id}
              item={item}
              onEdit={() => { setEditing(item); setModalOpen(true); }}
              onDelete={() => del(item)}
              onTest={() => test(item)}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      <CredentialModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        kinds={kinds}
        services={services}
        onSaved={load}
      />
    </div>
  );
}
